import type { MouseEvent, ReactNode } from 'react'

import type { GrowerPayment } from '@/lib/api/grower-payment-api'
import { Badge } from '@/components/ui/badge'
import { formatDateTimeVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    payment: GrowerPayment
    /** Nút Sửa / Xoá bên phải header. */
    actions?: ReactNode
    /**
     * Tap vào card → mở form sửa. Cụm actions tự chặn nổi bọt nên bấm Xoá không
     * kéo theo mở form.
     */
    onClick?: () => void
}

/**
 * Card mobile cho một phiếu chuyển khoản trả hộ.
 *
 * Bám cấu trúc thị giác của LotListCard (header + ô số liệu nền primary + dòng
 * meta mờ) nhưng không dùng lại được component đó: ô số liệu bên kia gắn cứng
 * nhãn "Còn lại" và format kiểu số lượng kg, còn đây là tiền VND và cần thêm
 * chỗ cho ảnh bill.
 */
export function GrowerPaymentListCard({ payment, actions, onClick }: Props) {
    const bank = [payment.bank_number, payment.bank_name]
        .filter(Boolean)
        .join(' · ')
    const clickable = Boolean(onClick)
    const stopPropagation = (e: MouseEvent) => e.stopPropagation()

    return (
        <div
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            className={`bg-card space-y-2 rounded-md border p-3 ${
                clickable ? 'hover:bg-muted/50 cursor-pointer' : ''
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="font-medium break-words">
                        {payment.grower_name}
                    </div>
                    {bank && (
                        <div className="text-muted-foreground mt-0.5 text-sm break-words">
                            {bank}
                        </div>
                    )}
                </div>
                {actions && (
                    <div
                        className="flex shrink-0 items-center gap-1"
                        onClick={stopPropagation}
                    >
                        {actions}
                    </div>
                )}
            </div>

            <div className="bg-primary/10 rounded-md px-3 py-2">
                <div className="text-muted-foreground text-xs">
                    {m.grower_payment_amount()}
                </div>
                <div className="text-primary text-lg font-semibold tabular-nums">
                    {formatVnd(payment.amount)}
                </div>
            </div>

            <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground text-xs">
                    {formatDateTimeVN(payment.transferred_at)}
                </span>
                {payment.bill_image_url ? (
                    <a
                        href={payment.bill_image_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={stopPropagation}
                    >
                        <img
                            src={payment.bill_image_url}
                            alt={m.grower_payment_bill()}
                            className="size-10 rounded object-cover"
                        />
                    </a>
                ) : (
                    <Badge variant="outline">
                        {m.grower_payment_bill_missing()}
                    </Badge>
                )}
            </div>
        </div>
    )
}
